/**
=========================================================
* Material Dashboard 2 PRO React - v2.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-material-ui
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import { createTheme } from "@mui/material/styles";
// import Fade from "@mui/material/Fade";

// Material Dashboard 2 PRO React TS Base Styles
import borders from "app/themes/theme-light/base/borders";
import boxShadows from "app/themes/theme-light/base/boxShadows";
import breakpoints from "app/themes/theme-light/base/breakpoints";
import colors from "app/themes/theme-light/base/colors";
import globals from "app/themes/theme-light/base/globals";
import typography from "app/themes/theme-light/base/typography";

// Material Dashboard 2 PRO React TS Helper Functions
import boxShadow from "app/themes/theme-light/functions/boxShadow";
import hexToRgb from "app/themes/theme-light/functions/hexToRgb";
import linearGradient from "app/themes/theme-light/functions/linearGradient";
import pxToRem from "app/themes/theme-light/functions/pxToRem";
import rgba from "app/themes/theme-light/functions/rgba";

// Material Dashboard 2 PRO React TS components base styles for @mui material components
import appBar from "app/themes/theme-light/components/appBar";
import avatar from "app/themes/theme-light/components/avatar";
import breadcrumbs from "app/themes/theme-light/components/breadcrumbs";
import button from "app/themes/theme-light/components/button";
import buttonBase from "app/themes/theme-light/components/buttonBase";
import card from "app/themes/theme-light/components/card";
import cardContent from "app/themes/theme-light/components/card/cardContent";
import cardMedia from "app/themes/theme-light/components/card/cardMedia";
import container from "app/themes/theme-light/components/container";
import dialog from "app/themes/theme-light/components/dialog";
import dialogActions from "app/themes/theme-light/components/dialog/dialogActions";
import dialogContent from "app/themes/theme-light/components/dialog/dialogContent";
import dialogContentText from "app/themes/theme-light/components/dialog/dialogContentText";
import dialogTitle from "app/themes/theme-light/components/dialog/dialogTitle";
import divider from "app/themes/theme-light/components/divider";
import flatpickr from "app/themes/theme-light/components/flatpickr";
import autocomplete from "app/themes/theme-light/components/form/autocomplete";
import checkbox from "app/themes/theme-light/components/form/checkbox";
import formControlLabel from "app/themes/theme-light/components/form/formControlLabel";
import formLabel from "app/themes/theme-light/components/form/formLabel";
import input from "app/themes/theme-light/components/form/input";
import inputLabel from "app/themes/theme-light/components/form/inputLabel";
import inputOutlined from "app/themes/theme-light/components/form/inputOutlined";
import radio from "app/themes/theme-light/components/form/radio";
import select from "app/themes/theme-light/components/form/select";
import switchButton from "app/themes/theme-light/components/form/switchButton";
import textField from "app/themes/theme-light/components/form/textField";
import icon from "app/themes/theme-light/components/icon";
import iconButton from "app/themes/theme-light/components/iconButton";
import linearProgress from "app/themes/theme-light/components/linearProgress";
import link from "app/themes/theme-light/components/link";
import list from "app/themes/theme-light/components/list";
import listItem from "app/themes/theme-light/components/list/listItem";
import listItemText from "app/themes/theme-light/components/list/listItemText";
import menu from "app/themes/theme-light/components/menu";
import menuItem from "app/themes/theme-light/components/menu/menuItem";
import popover from "app/themes/theme-light/components/popover";
import sidenav from "app/themes/theme-light/components/sidenav";
import slider from "app/themes/theme-light/components/slider";
import stepper from "app/themes/theme-light/components/stepper";
import step from "app/themes/theme-light/components/stepper/step";
import stepConnector from "app/themes/theme-light/components/stepper/stepConnector";
import stepIcon from "app/themes/theme-light/components/stepper/stepIcon";
import stepLabel from "app/themes/theme-light/components/stepper/stepLabel";
import svgIcon from "app/themes/theme-light/components/svgIcon";
import tableCell from "app/themes/theme-light/components/table/tableCell";
import tableContainer from "app/themes/theme-light/components/table/tableContainer";
import tableHead from "app/themes/theme-light/components/table/tableHead";
import tabs from "app/themes/theme-light/components/tabs";
import tab from "app/themes/theme-light/components/tabs/tab";
import tooltip from "app/themes/theme-light/components/tooltip";

export default createTheme({
  direction: "rtl",
  breakpoints: { ...breakpoints },
  palette: { ...colors },
  typography: { ...typography },
  boxShadows: { ...boxShadows },
  borders: { ...borders },
  functions: {
    boxShadow,
    hexToRgb,
    linearGradient,
    pxToRem,
    rgba
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ...globals,
        ...flatpickr,
        ...container
      }
    },
    MuiDrawer: { ...sidenav },
    MuiList: { ...list },
    MuiListItem: { ...listItem },
    MuiListItemText: { ...listItemText },
    MuiCard: { ...card },
    MuiCardMedia: { ...cardMedia },
    MuiCardContent: { ...cardContent },
    MuiButton: { ...button },
    MuiIconButton: { ...iconButton },
    MuiInput: { ...input },
    MuiInputLabel: { ...inputLabel },
    MuiOutlinedInput: { ...inputOutlined },
    MuiTextField: { ...textField },
    MuiMenu: { ...menu },
    MuiMenuItem: { ...menuItem },
    MuiSwitch: { ...switchButton },
    MuiDivider: { ...divider },
    MuiTableContainer: { ...tableContainer },
    MuiTableHead: { ...tableHead },
    MuiTableCell: { ...tableCell },
    MuiLinearProgress: { ...linearProgress },
    MuiBreadcrumbs: { ...breadcrumbs },
    MuiSlider: { ...slider },
    MuiAvatar: { ...avatar },
    MuiTooltip: { ...tooltip },
    MuiAppBar: { ...appBar },
    MuiTabs: { ...tabs },
    MuiTab: { ...tab },
    MuiStepper: { ...stepper },
    MuiStep: { ...step },
    MuiStepConnector: { ...stepConnector },
    MuiStepLabel: { ...stepLabel },
    MuiStepIcon: { ...stepIcon },
    MuiSelect: { ...select },
    MuiFormControlLabel: { ...formControlLabel },
    MuiFormLabel: { ...formLabel },
    MuiCheckbox: { ...checkbox },
    MuiRadio: { ...radio },
    MuiAutocomplete: { ...autocomplete },
    MuiPopover: { ...popover },
    MuiButtonBase: { ...buttonBase },
    MuiIcon: { ...icon },
    MuiSvgIcon: { ...svgIcon },
    MuiLink: { ...link },
    MuiDialog: { ...dialog },
    MuiDialogTitle: { ...dialogTitle },
    MuiDialogContent: { ...dialogContent },
    MuiDialogContentText: { ...dialogContentText },
    MuiDialogActions: { ...dialogActions }
  }
});
